import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  answerBlock
} from 'actions';

import Question from 'components/Questionnaires/Question';
import { validateQuestion } from 'components/Questionnaires/validators';

export const QuestionRepeater = ({ block, preQualAnswers, messageValues }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const [, setForceRender] = useState(null);
  const [answer, setAnswer] = useState(null);
  const questions = useRef([]);

  const onQuestionChange = (targetQuestion, value) => {
    const questionIndex = questions.current.findIndex(b => b.question.index === targetQuestion.index);
    if (questionIndex > -1) {
      const newQuestion = { ...questions.current[questionIndex].question, ...{ answer: value } };
      validateQuestion(newQuestion);
      questions.current[questionIndex].question = newQuestion;
      questions.current[questionIndex].component = <Question
        key={ `${ block.id }-${ questionIndex }-${ newQuestion.id }` }
        blockId={ `${ block.id }-${ questionIndex }` }
        question={ newQuestion }
        { ...newQuestion.componentProps }
        messageValues={ messageValues }
        onChange={ (question, changedValue) => onQuestionChange(question, changedValue) }
      />;
      setAnswer(questions.current.map(b => b.question.answer || {}));
    }
  };

  const defaultQuestions = () => {
    if (block.minSize > 0) {
      return Array.from({ length: block.minSize ?? 0 }, (_, i) => {
        const newQuestion = Object.assign({ index: i, answer: undefined }, block.question);
        return {
          question: newQuestion,
          component: <Question
            key={ `${ block.id }-${ i }-${ newQuestion.id }` }
            blockId={ `${ block.id }-${ i }` }
            question={ newQuestion }
            { ...newQuestion.componentProps }
            messageValues={ messageValues }
            onChange={ (question, changedValue) => onQuestionChange(question, changedValue) }
          />
        };
      });
    }
    return [];
  };

  const onAddQuestion = () => {
    const newQuestions = [...questions.current];
    const nextIndex = questions.current.length === 0 ? 0 : questions.current[questions.current.length - 1].question.index + 1;
    setAnswer([...answer, undefined]);
    const newQuestion = Object.assign({ index: nextIndex }, block.question);
    newQuestions.push({
      question: newQuestion,
      component: <Question
        key={ `${ block.id }-${ nextIndex }-${ newQuestion.id }` }
        blockId={ `${ block.id }-${ nextIndex }` }
        question={ newQuestion }
        { ...newQuestion.componentProps }
        messageValues={ messageValues }
        onChange={ (question, changedValue) => onQuestionChange(question, changedValue) }
      />
    });
    questions.current = newQuestions;
  };

  const onRemoveQuestion = (i) => {
    const newAnswer = [...answer];
    newAnswer.splice(i, 1);
    setAnswer(newAnswer);
    const newQuestions = [...questions.current];
    newQuestions.splice(i, 1);
    questions.current = newQuestions;
  };

  useEffect(() => {
    const initialQuestions = defaultQuestions();
    questions.current = [...initialQuestions];
    setAnswer(new Array(block.minSize).fill(undefined));
  }, []);

  useEffect(() => {
    dispatch(answerBlock(block.id, answer, !questions.current.some(b => b.question.isValid === false)));
  }, [answer]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      for (let i = 0; i < questions.current.length; i += 1) {
        const newQuestion = { ...questions.current[i].question, ...{ answer: questions.current[i].question.answer ?? '', isForceEdited: true } };
        validateQuestion(newQuestion);
        questions.current[i].question = newQuestion;
        questions.current[i].component = <Question
          key={ `${ block.id }-${ i }-${ newQuestion.id }` }
          blockId={ `${ block.id }-${ i }` }
          question={ newQuestion }
          { ...newQuestion.componentProps }
          messageValues={ messageValues }
          onChange={ (question, changedValue) => onQuestionChange(question, changedValue) }
        />;
      }
      setAnswer(questions.current.map(b => b.question.answer || {}));
      setForceRender(new Date().getTime());
    }
  }, [block.isForceEdited]);

  return <div className="repeater">
    { questions.current.map((q, i) => <div className="repeater-item" key={ `${ q.question.id }-${ q.question.index }` }>
      <div className="row">
        <div className="col-11">
          { q.component }
        </div>
        <div className="col-1">
          { (i + 1) > (block.minSize ?? 0) && <button className="btn-repeater" type="button" onClick={ () => onRemoveQuestion(i) } title={ intl.formatMessage({ id: 'questionnaire.repeater.remove' }) }>
            <FontAwesomeIcon icon="trash-alt" className="fa-xs" />
          </button> }
        </div>
      </div>
    </div>) }
    { (!block.maxSize || questions.current.length < block.maxSize) && <button className="btn-repeater btn-add" type="button" onClick={ () => onAddQuestion() } title={ intl.formatMessage({ id: 'questionnaire.repeater.add' }) }>
      <FontAwesomeIcon icon="plus" className="fa-xs" />
    </button> }
  </div>;
};

QuestionRepeater.displayName = 'QuestionRepeater';

QuestionRepeater.propTypes = {
  block: PropTypes.shape().isRequired,
  preQualAnswers: PropTypes.shape(),
  messageValues: PropTypes.shape(),
};

QuestionRepeater.defaultProps = {
  preQualAnswers: null,
  messageValues: {}
};

export default QuestionRepeater;
